import { Container, Graphics, Text } from 'pixi.js';
import {
  OFFICE_DOORS,
  OFFICE_OBJECTS,
  OFFICE_WALLS,
  OFFICE_ZONES,
  SIM,
  WALL_THICKNESS,
  type DecorItem,
  type InteractiveObject,
  type Level,
  type LevelLayout,
  type LevelId,
  type WorldDoor,
  type WorldTheme,
  type WorldWall,
  type WorldZone,
} from '@duck/contracts';
import { darken, lighten, mix, themeArt, type ThemeArt } from './theme-art';
import { buildPropBody } from './props';
import { buildDecorItem, isFloorDecor } from './decor-catalog';
import type { DecorTagged } from './furniture';
import { decorateRoom } from './rooms';

/**
 * The office level — drawn ENTIRELY in code (ADR-0010 procedural MVP; commissioned/AI textures slot in
 * behind these same shapes later). The room GEOMETRY + names + the `isolated` rule come from the SHARED
 * {@link OFFICE_ZONES} contract, so "which room is drawn here" can never drift from "which room the
 * server gates voice for" (ADR-0009). This file owns only PRESENTATION.
 *
 * What it builds, bottom to top, to make the world feel like a PLACE rather than a flat plane:
 *   1. a textured FLOOR (planks / panels / cobble, themed) with a soft vignette;
 *   2. a back WALL band with trim + EXTERIOR WINDOWS that look onto the themed sky (a skyline, a
 *      starfield, gothic arches, a sunlit street) — the sense of an outside world;
 *   3. per-room FLOORS (a warm/cool tint + an area RUG) with drop-shadow depth + a baseboard so each
 *      room sits in the level, plus per-ROOM character furniture (dispatched via the {@link './rooms'}
 *      registry — one module per room — a meeting table, lounge plants, …);
 *   4. the WALLS + DOOR openings (World Foundation): solid themed wall slabs from {@link OFFICE_WALLS}
 *      with a top highlight so they read as standing walls, and themed door frames/thresholds in the
 *      {@link OFFICE_DOORS} gaps so every entrance reads as a real doorway;
 *   5. the interactive PROPS (real furniture) under their preserved dual-coded affordance cue;
 *   6. the isolated-nook treatment (dashed seal + 🔒 label) and the legible room labels.
 *
 * M8: the SAME layout, re-skinned by the guild's `theme` (palette + material/window/glow only — never
 * geometry/mechanics). The engine rebuilds this container on a real theme change (cheap; not per frame),
 * so an owner's theme switch re-skins the world live for everyone. Per-room HUE is preserved so rooms
 * stay distinguishable while the whole palette shifts to the chosen world.
 *
 * Accessibility (ADR-0012): room/object labels keep their dark stroke so they stay legible under the
 * M4.2 night wash and every theme; the interaction pads use the theme accent but never replace the text
 * cue (dual-coded). All decoration sits BELOW the ducks/labels in the engine's z-order.
 */

/** Per-room base HUE bias (presentation only). The theme re-skin blends these toward the theme wall.
 *  Castle Hearthhold (ADR-0051): each room leans toward its dressing brief's mood — warm forge/kitchen
 *  oranges, cool blue-grey focus/cellar, rose chapel, honey-gold hearth/vault — while the theme blend
 *  pulls them all into one honey-grey stone family so they stay a cohesive keep. */
const ROOM_COLOR: Record<string, number> = {
  // ── CENTRE — the warm honey-gold heart (the hero hall is the warmest, richest, most dominant) ──
  'throne-hall': 0x7a5a2e, // rich warm gold — the hero hall, visibly dominant
  'grand-court': 0x6e5c38, // warm honey-sandstone plaza (a welcoming commons, not cold)
  'north-gallery': 0x68583a, // warm cloister stone
  'west-cloister': 0x645438, // warm arcade
  'east-cloister': 0x645438, // warm arcade
  'lower-bailey': 0x6e5a34, // warm market forecourt
  'north-rampart': 0x5a6678, // cool sky-grey high wall-walk
  // ── Towers ──
  'watch-tower': 0x46464f, // dim iron lookout
  'astronomy-tower': 0x33335e, // deep night blue-violet
  'mage-tower': 0x3a2e54, // arcane violet
  'royal-tower': 0x5a4658, // regal purple-warm
  // ── WEST WING (martial — iron + forge warmth) ──
  armoury: 0x6e4626, // forge orange
  barracks: 0x5a4632, // cosy warm-neutral mess
  'training-yard': 0x645a3c, // dusty outdoor yard
  'guard-hall': 0x42424c, // cold iron stair-down hall
  // ── North band ──
  chapel: 0x6a4a5e, // soft rose
  'war-room': 0x68543a, // warm council
  // ── EAST WING (scholarly = cool blue-slate; green = gardens) ──
  'great-library': 0x36425e, // cool blue quiet
  scriptorium: 0x3e4660, // cool blue-grey focus
  'alchemy-lab': 0x3a4e48, // green-tinged arcane
  'royal-gardens': 0x466038, // garden green
  orchard: 0x506638, // warm orchard green
  // ── THE UNDERCROFT (genuinely dark — see DARK_ROOMS) + the gate ──
  'dungeon-jail': 0x222229, // near-black cold cell
  catacombs: 0x26242e, // near-black crypt
  'wine-cellar': 0x322c38, // dark undercroft
  'treasury-vault': 0x44351f, // dark warm — so the relic's gold pops out of shadow
  gatehouse: 0x6a5436, // warm honey-flagstone — the welcoming barbican (the spawn's first frame)
};
const DEFAULT_ROOM_COLOR = 0x5a4c3a;

/** Undercroft chambers rendered MUCH darker (near-black floor) so torch/relic glow-pools read as pools of
 *  light in darkness — the dungeon/descent mood the brief asks for (CD review). */
const DARK_ROOMS = new Set(['dungeon-jail', 'catacombs', 'wine-cellar', 'treasury-vault']);

/** How strongly a room's base hue is pulled toward the theme's wall colour (0 = office, 1 = pure wall).
 *  Lowered so each district keeps its OWN signature tint (identity reads from the map — CD review). */
const ROOM_THEME_BLEND = 0.3;
/** How strongly the floor base is pulled toward the theme's floor colour. */
const FLOOR_THEME_BLEND = 0.85;

/**
 * Re-skin one room's base office hue toward the active theme: blend it toward the theme's WALL colour so
 * every world has its own palette, while keeping each room's distinct lean so they stay legible.
 */
function skinRoomColor(base: number, theme: WorldTheme): number {
  return mix(base, theme.palette.wall, ROOM_THEME_BLEND);
}

/**
 * The shipped office's render layout (zones + derived walls + doors + objects). The DEFAULT for
 * {@link createOfficeMap}, so the live game's call site stays `createOfficeMap(theme)` and renders
 * byte-identically; the LEVEL EDITOR passes a different {@link LevelLayout} (from an authored
 * `LevelDocument`) to preview an arbitrary floor plan through this SAME renderer (true WYSIWYG).
 */
const OFFICE_LAYOUT: LevelLayout = {
  zones: OFFICE_ZONES,
  walls: OFFICE_WALLS,
  doors: OFFICE_DOORS,
  objects: OFFICE_OBJECTS,
  // Empty: the LIVE office renders its furniture through the decorators (original path). The editor passes
  // its OWN layout (with captured furniture in `decor`), which switches on the data path below.
  decor: [],
};

/**
 * The render layout for a resolved {@link Level} (office or castle) — the geometry the procedural map
 * draws, taken straight off the level's derived structures. `decor` is empty: the LIVE game renders each
 * room's furniture through the per-room DECORATORS (the original path), so a non-empty `decor` is the
 * editor's data-mode signal (see {@link createOfficeMap}). The engine resolves the level from the synced
 * `levelId` and hands the result here, so the SAME renderer draws either world without forking a path.
 */
export function layoutForLevel(level: Level): LevelLayout {
  return {
    zones: level.zones,
    walls: level.walls,
    doors: level.doors,
    objects: level.objects,
    decor: [],
  };
}

/**
 * Build the full themed map for a level layout (the office by default). Layers are added bottom-to-top
 * within this one container; the engine keeps the whole container at the bottom of the world (below
 * ambient/event washes + the ducks), so none of this decoration ever occludes or dims the foreground.
 *
 * The geometry is read ENTIRELY from `layout` (no module-level constants in the draw path), so the same
 * renderer draws the live office and an editor's authored level identically — only the data differs.
 */
export function createOfficeMap(
  theme: WorldTheme,
  layout: LevelLayout = OFFICE_LAYOUT,
  worldWidth: number = SIM.WORLD_WIDTH,
  worldHeight: number = SIM.WORLD_HEIGHT,
  opts: { suppressDecor?: boolean; bareLevel?: boolean; levelId?: LevelId } = {},
): Container {
  const map = new Container();
  const art = themeArt(theme);
  // Which level's decorators to dispatch (level-aware — the two castles reuse room ids). Default: office.
  const levelId: LevelId = opts.levelId ?? 'office';

  // DATA mode (the editor passes a layout WITH captured furniture in `decor`): the room FURNITURE comes
  // from `layout.decor` (editable data) instead of the decorators, so every piece is movable/deletable.
  // The decorators still paint their bespoke scenery (counters, racks, conference tables) — only their
  // CATALOG furniture is suppressed. The LIVE game (office OR castle) passes a layout with EMPTY decor
  // ({@link layoutForLevel}), keeping the original per-room decorator path; so a non-empty `decor` is the
  // editor's data-mode signal. NOTE (dual-level): we key off `decor.length`, NOT `layout !== OFFICE_LAYOUT`,
  // because the live CASTLE also passes a non-OFFICE_LAYOUT — `===` would wrongly treat it as editor data.
  const dataMode = layout.decor.length > 0;
  // SUPPRESS DECOR (live office, "unbaked furniture"): drop the decorators' tagged furniture + scenery from
  // the baked backdrop so the SAME pieces — now seeded as editable PlacedFurniture (OFFICE_DECOR) — render
  // in the live FurnitureLayer instead (movable/deletable). Untagged backdrop (counters, floor accents),
  // walls, objects + seats stay baked. Distinct from dataMode: we don't render data decor INTO the map here.
  const suppressDecor = dataMode || opts.suppressDecor === true;
  // BARE LEVEL (live office): a truly EMPTY room — drop ALL decorator output (both the tagged catalog
  // furniture AND the untagged painted/architectural backdrop the room modules hardcode: counters, upper
  // cabinets, menu boards, sinks, shelves, pot racks, pendant lamps, the entry bell, …) AND the interactive
  // OBJECTS (coffee/workstation/sofa/tv/arcade/jukebox). Only the structure stays: floor, room panels,
  // walls + doors, the isolated-room seal, and the room label. The CASTLE passes bareLevel=false so its rich
  // per-room decorators (hearth, forge, throne, …) still bake; only the office is bare (store-furnished).
  const bareLevel = opts.bareLevel === true;

  const floorBase = mix(0x141d36, theme.palette.floor, FLOOR_THEME_BLEND);
  const floorRim = mix(floorBase, art.accent, 0.25);

  drawFloor(map, art, floorBase, floorRim, worldWidth, worldHeight);
  const byZone = bucketDecorByZone(layout);

  // LAYER ORDER (bottom → top): floor → room floor PANELS → WALLS+doors → room CONTENTS (furniture/decor) →
  // labels → interactive objects. Walls sit ABOVE the floor panels (so a wall reads as standing on the
  // floor) but BELOW everything placeable — so any object/furniture ON or NEXT TO a wall renders OVER the
  // wall and is never clipped by the wall texture.

  // Pass 1: room floor panels only (under the walls).
  for (const zone of layout.zones) {
    drawRoomPanel(map, zone, theme, art);
  }

  // Walls + door openings — below the furniture/objects (the whole point: nothing placed on a wall is
  // clipped by it). Still BELOW the ducks (the engine keeps the whole map under the actors).
  drawWalls(map, art, layout.walls);
  drawDoors(map, art, layout.doors, layout.zones);

  // Pass 2: room CONTENTS above the walls — rugs (under furniture), bespoke scenery, furniture/decor, then
  // the isolated-nook seal + the legible room label.
  for (const zone of layout.zones) {
    if (bareLevel) {
      // Empty room: no decorator output at all (tagged or untagged) — just the seal + label below.
    } else if (suppressDecor) {
      // Drop the decorators' catalog furniture + tagged scenery (it renders live / as data instead).
      const bucket = byZone.zones.get(zone.id) ?? { rugs: [], furniture: [] };
      // In DATA mode the editor's own decor is rendered into the map; in live suppress mode it is NOT
      // (the FurnitureLayer draws the seeded pieces over the map), so both buckets are empty there.
      for (const it of bucket.rugs) map.addChild(buildDecorItem(it, art));
      decorateRoomSuppressed(map, zone, art, levelId);
      for (const it of bucket.furniture) map.addChild(buildDecorItem(it, art));
    } else {
      decorateRoom(map, zone, art, levelId);
    }
    drawRoomSeal(map, zone);
    drawRoomLabel(map, zone);
  }
  // Decor in the open commons (no room) — also above the walls.
  if (dataMode) {
    for (const it of byZone.outside) map.addChild(buildDecorItem(it, art));
  }

  // Interactive objects — the very top of the map. Skipped on a bare level (the coffee machine / jukebox /
  // TV / arcade are buyable from the Furniture Store instead, so the default room ships none).
  if (!bareLevel) {
    for (const obj of layout.objects) {
      drawObject(map, obj, art);
    }
  }

  return map;
}

/** Bucket a layout's decor into per-room {rugs, furniture} (+ a commons "outside" list) by point-in-zone. */
function bucketDecorByZone(layout: LevelLayout): {
  zones: Map<string, { rugs: DecorItem[]; furniture: DecorItem[] }>;
  outside: DecorItem[];
} {
  const zones = new Map<string, { rugs: DecorItem[]; furniture: DecorItem[] }>();
  for (const z of layout.zones) zones.set(z.id, { rugs: [], furniture: [] });
  const outside: DecorItem[] = [];
  for (const d of layout.decor) {
    const z = layout.zones.find(
      (zz) => d.x >= zz.x && d.x < zz.x + zz.width && d.y >= zz.y && d.y < zz.y + zz.height,
    );
    const bucket = z ? zones.get(z.id) : undefined;
    if (!bucket) {
      outside.push(d);
      continue;
    }
    (isFloorDecor(d.kind) ? bucket.rugs : bucket.furniture).push(d);
  }
  return { zones, outside };
}

/**
 * Reparent a room's decorator output into `map`, DROPPING any tagged catalog furniture (rendered from
 * decor data instead). Bespoke scenery + backdrop (untagged) pass through, preserving their look + order.
 */
function decorateRoomSuppressed(map: Container, zone: WorldZone, art: ThemeArt, levelId: LevelId): void {
  const tmp = new Container();
  decorateRoom(tmp, zone, art, levelId);
  for (const child of [...tmp.children]) {
    if ((child as unknown as DecorTagged).__decorKind === undefined) map.addChild(child);
  }
  tmp.destroy({ children: true }); // drop the suppressed (tagged) furniture
}

// ── Floor ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * The world floor: a base fill, a cheap repeating MATERIAL pattern (planks / tile grid / metal panels /
 * arcane cobble) drawn into ONE Graphics (single draw call), and a soft inner vignette so the room edges
 * fall away gently. Pattern lines are faint (alpha ≈ 0.06–0.1) so they read as texture, never as noise.
 */
function drawFloor(
  map: Container,
  art: ThemeArt,
  floorBase: number,
  floorRim: number,
  W: number,
  H: number,
): void {

  map.addChild(
    new Graphics().roundRect(0, 0, W, H, 24).fill(floorBase).stroke({ color: floorRim, width: 6 }),
  );

  // The material pattern, all in one Graphics → one draw call for the whole floor.
  const pat = new Graphics();
  if (art.floor === 'planks') {
    // Long wood planks: alternating tone bands + faint seam lines lengthwise.
    const plank = 56;
    for (let y = 0, row = 0; y < H; y += plank, row++) {
      pat.rect(0, y, W, plank).fill({ color: row % 2 ? art.floorB : art.floorA, alpha: 0.5 });
    }
    for (let y = plank; y < H; y += plank) {
      pat.rect(0, y - 0.5, W, 1).fill({ color: art.floorSeam, alpha: 0.5 });
    }
    // Stagger short seams across planks so it reads as boards, not stripes.
    for (let y = 0, row = 0; y < H; y += plank, row++) {
      const off = (row % 2) * 130;
      for (let x = off; x < W; x += 260) {
        pat.rect(x, y, 1, plank).fill({ color: art.floorSeam, alpha: 0.35 });
      }
    }
  } else if (art.floor === 'tiles') {
    const t = 64;
    for (let y = 0, ry = 0; y < H; y += t, ry++) {
      for (let x = 0, rx = 0; x < W; x += t, rx++) {
        pat.rect(x, y, t, t).fill({ color: (rx + ry) % 2 ? art.floorB : art.floorA, alpha: 0.45 });
      }
    }
  } else if (art.floor === 'panels') {
    // Brushed metal deck panels: a clean grid with bright top/left seams + bolt dots.
    const t = 96;
    for (let x = 0; x <= W; x += t) {
      pat.rect(x - 0.5, 0, 1.5, H).fill({ color: lighten(art.floorSeam, 0.3), alpha: 0.4 });
    }
    for (let y = 0; y <= H; y += t) {
      pat.rect(0, y - 0.5, W, 1.5).fill({ color: lighten(art.floorSeam, 0.3), alpha: 0.4 });
    }
    for (let y = t; y < H; y += t) {
      for (let x = t; x < W; x += t) {
        pat.circle(x, y, 1.6).fill({ color: art.accent, alpha: 0.25 });
      }
    }
  } else {
    // Cobble: staggered rounded stones in two close tones (the academy's flagstone floor).
    const s = 52;
    for (let y = 0, row = 0; y < H; y += s, row++) {
      const off = (row % 2) * (s / 2);
      for (let x = -off; x < W; x += s) {
        pat
          .roundRect(x + 3, y + 3, s - 6, s - 6, 7)
          .fill({ color: (x + y) % (s * 2) ? art.floorB : art.floorA, alpha: 0.5 });
      }
    }
  }
  map.addChild(pat);

  // Soft inner vignette — a darker rim hugging the floor edge so the level falls away gently (the room
  // feels enclosed, not like an infinite plane). One stroked rounded-rect, very low alpha.
  map.addChild(
    new Graphics()
      .roundRect(8, 8, W - 16, H - 16, 22)
      .stroke({ color: darken(floorBase, 0.4), width: 40, alpha: 0.18 }),
  );
}

// ── Rooms ─────────────────────────────────────────────────────────────────────────────────────────

/** The room floor: drop shadow + tinted panel with inner light/shade + rim/baseboard. District tints are
 *  laid strongly (so the scholarly east, martial west, green gardens + warm centre read apart at a glance);
 *  undercroft chambers ({@link DARK_ROOMS}) are floored near-black so their glow-pools pop in the dark. */
function drawRoomPanel(map: Container, zone: WorldZone, theme: WorldTheme, art: ThemeArt): void {
  const color = skinRoomColor(ROOM_COLOR[zone.id] ?? DEFAULT_ROOM_COLOR, theme);
  const dark = DARK_ROOMS.has(zone.id);
  const fillAlpha = dark ? 0.9 : 0.62; // dark chambers nearly opaque (kill the warm world floor beneath)

  // Drop shadow: the room panel floats just-so above the floor, grounding it.
  map.addChild(
    new Graphics()
      .roundRect(zone.x + 4, zone.y + 6, zone.width, zone.height, 16)
      .fill({ color: 0x000000, alpha: 0.18 }),
  );

  // The room floor panel: a strong district tint + a bright inner top edge (light) and a darker base (AO),
  // plus a crisp rim so the room reads as a defined space. Dark chambers skip the top light (stay gloomy).
  const panel = new Graphics()
    .roundRect(zone.x, zone.y, zone.width, zone.height, 16)
    .fill({ color, alpha: fillAlpha });
  if (!dark) {
    panel
      .roundRect(zone.x + 3, zone.y + 3, zone.width - 6, 10, 10)
      .fill({ color: lighten(color, 0.25), alpha: 0.2 }); // top inner light
  }
  panel
    // bottom inner shade (ambient occlusion)
    .roundRect(zone.x + 3, zone.y + zone.height - 14, zone.width - 6, 11, 10)
    .fill({ color: 0x000000, alpha: dark ? 0.3 : 0.14 })
    // rim + baseboard
    .roundRect(zone.x, zone.y, zone.width, zone.height, 16)
    .stroke({ color, width: 3, alpha: 0.9 })
    .roundRect(zone.x + 1.5, zone.y + 1.5, zone.width - 3, zone.height - 3, 14)
    .stroke({ color: art.baseboard, width: 2, alpha: 0.5 });
  map.addChild(panel);
}

/** A private chamber (war-room, cellar, vault, gatehouse) gets a barely-there warm INSET border — a quiet
 *  "this room is sealed/intimate" recessed frame (reinforced by the 🔒 in the label). NEVER a loud dashed
 *  debug stroke (that read as a dev overlay and broke immersion — CD review round 1). */
function drawRoomSeal(map: Container, zone: WorldZone): void {
  if (!zone.isolated) return;
  const inset = 9;
  map.addChild(
    new Graphics()
      .roundRect(zone.x + inset, zone.y + inset, zone.width - inset * 2, zone.height - inset * 2, 12)
      .stroke({ color: 0x000000, width: 2, alpha: 0.09 }),
  );
}

/** Room label — a dark plate behind night-safe text (ADR-0012), at the room's top-left. */
function drawRoomLabel(map: Container, zone: WorldZone): void {
  const label = new Text({
    text: zone.isolated ? `🔒 ${zone.name}` : zone.name,
    style: {
      fill: 0xeef1ff,
      fontSize: 18,
      fontWeight: '700',
      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
      stroke: { color: 0x0b1020, width: 3 },
    },
  });
  const plate = new Graphics()
    .roundRect(zone.x + 8, zone.y + 8, label.width + 16, label.height + 8, 8)
    .fill({ color: 0x0b1020, alpha: 0.32 });
  label.x = zone.x + 16;
  label.y = zone.y + 12;
  map.addChild(plate, label);
}

// ── Walls + doors (World Foundation "Walls + Rooms") ──────────────────────────────────────────────

/**
 * Draw every solid wall slab from the SHARED {@link OFFICE_WALLS} (the same rects the server + the client
 * prediction collide against — geometry can never drift from collision). Each slab is a themed solid with
 * a thin LIT top edge + a darker base so it reads as a standing wall rather than a flat stripe, plus a
 * soft drop shadow onto the floor below so it sits in the level. Everything is batched into a SMALL set of
 * pooled Graphics (one for shadows, one for bodies, one for highlights) → a handful of draw calls for all
 * ~62 slabs, built ONCE per theme (never per frame). Walls live BELOW the ducks (the map container is
 * under the actors), so they BLOCK ducks without ever occluding them.
 */
function drawWalls(map: Container, art: ThemeArt, walls: readonly WorldWall[]): void {
  const wallBody = mix(art.wall, art.baseboard, 0.35); // a touch darker than the back-wall band → "interior" walls
  const hi = lighten(wallBody, 0.28);
  const lo = darken(wallBody, 0.3);

  const shadows = new Graphics();
  const bodies = new Graphics();
  const edges = new Graphics();

  for (const w of walls) {
    // Drop shadow just below/right so the slab grounds onto the floor.
    shadows.roundRect(w.x + 2, w.y + 3, w.width, w.height, 3).fill({ color: 0x000000, alpha: 0.22 });
    // Body + a darker base band (ambient occlusion at the foot of the wall).
    bodies
      .roundRect(w.x, w.y, w.width, w.height, 3)
      .fill(wallBody)
      .rect(w.x, w.y + w.height - 3, w.width, 3)
      .fill({ color: lo, alpha: 0.6 });
    // Lit top edge so the wall reads as catching the light (depth cue).
    edges.rect(w.x, w.y, w.width, Math.min(3, w.height)).fill({ color: hi, alpha: 0.85 });
  }

  map.addChild(shadows, bodies, edges);
}

/**
 * Draw a clear DOORWAY in each {@link OFFICE_DOORS} gap so an opening reads as a real entrance, not a
 * random hole in the wall. A door is the ABSENCE of wall (collision uses {@link OFFICE_WALLS} alone), so
 * this is purely presentation: per door we lay a warm FLOOR THRESHOLD strip across the opening, two short
 * JAMB posts in the theme wall tone flanking it (so the gap reads as framed), and a faint ARCH/lintel
 * highlight over the top — the dual cue "you can walk through here". All batched into pooled Graphics,
 * built once per theme. The threshold sits flush with the wall slab (thickness {@link WALL_THICKNESS}).
 */
function drawDoors(
  map: Container,
  art: ThemeArt,
  doors: readonly WorldDoor[],
  zones: readonly WorldZone[],
): void {
  const T = WALL_THICKNESS;
  const jamb = mix(art.wall, art.baseboard, 0.35);
  const jambHi = lighten(jamb, 0.28);
  const threshold = mix(art.floorA, art.accent, 0.3);

  const floor = new Graphics();
  const frame = new Graphics();

  for (const d of doors) {
    const z = zones.find((zz) => zz.id === d.zoneId);
    if (!z) continue;
    drawDoor(floor, frame, d, z, T, threshold, jamb, jambHi, art.accent);
  }

  map.addChild(floor, frame);
}

/** One doorway: a floor threshold across the gap + two jamb posts + a faint lintel arch, on the room edge. */
function drawDoor(
  floor: Graphics,
  frame: Graphics,
  d: WorldDoor,
  z: WorldZone,
  T: number,
  threshold: number,
  jamb: number,
  jambHi: number,
  accent: number,
): void {
  const half = d.width / 2;
  const post = 5; // jamb post thickness
  const arch = 3; // lintel highlight thickness

  if (d.side === 'N' || d.side === 'S') {
    // Horizontal opening along the room's top/bottom edge. The wall slab sits inset by T from the edge.
    const edgeY = d.side === 'N' ? z.y : z.y + z.height - T;
    const x0 = d.pos - half;
    // Warm floor threshold filling the opening's depth (the wall thickness).
    floor.roundRect(x0, edgeY, d.width, T, 2).fill({ color: threshold, alpha: 0.5 });
    // Jamb posts flanking the gap (each the wall thickness deep) + a lit inner edge.
    frame
      .rect(x0 - post, edgeY, post, T)
      .fill(jamb)
      .rect(x0, edgeY, 1.5, T)
      .fill({ color: jambHi, alpha: 0.8 })
      .rect(x0 + d.width, edgeY, post, T)
      .fill(jamb)
      .rect(x0 + d.width - 1.5, edgeY, 1.5, T)
      .fill({ color: jambHi, alpha: 0.8 })
      // Faint lintel arch across the top of the opening (accent) → "this is a doorway".
      .rect(x0, edgeY + (d.side === 'N' ? 0 : T - arch), d.width, arch)
      .fill({ color: accent, alpha: 0.35 });
  } else {
    // Vertical opening along the room's left/right edge.
    const edgeX = d.side === 'W' ? z.x : z.x + z.width - T;
    const y0 = d.pos - half;
    floor.roundRect(edgeX, y0, T, d.width, 2).fill({ color: threshold, alpha: 0.5 });
    frame
      .rect(edgeX, y0 - post, T, post)
      .fill(jamb)
      .rect(edgeX, y0, T, 1.5)
      .fill({ color: jambHi, alpha: 0.8 })
      .rect(edgeX, y0 + d.width, T, post)
      .fill(jamb)
      .rect(edgeX, y0 + d.width - 1.5, T, 1.5)
      .fill({ color: jambHi, alpha: 0.8 })
      .rect(edgeX + (d.side === 'W' ? 0 : T - arch), y0, arch, d.width)
      .fill({ color: accent, alpha: 0.35 });
  }
}

// ── Interactive objects ───────────────────────────────────────────────────────────────────────────

/**
 * One interactive object: the procedural PROP body (real furniture), beneath the PRESERVED dual-coded
 * affordance — a soft interaction pad on the floor in the theme accent + the emoji icon + a text label.
 * The pad is a static, low-key glow ring (not pulsing) so the "you can use this" read stays calm and the
 * Press-E cue (rendered in the HUD on proximity) remains the active signal.
 */
function drawObject(map: Container, obj: InteractiveObject, art: ThemeArt): void {
  // Interaction pad: a faint floor halo + a crisp accent ring → "interactable" at a glance, themed hue.
  const pad = new Graphics()
    .circle(obj.x, obj.y, 26)
    .fill({ color: art.accent, alpha: 0.08 })
    .circle(obj.x, obj.y, 18)
    .stroke({ color: art.accent, width: 2, alpha: 0.55 })
    .circle(obj.x, obj.y, 4)
    .fill({ color: lighten(art.accent, 0.2), alpha: 0.9 });
  map.addChild(pad);

  // The prop body, positioned at the object's floor point (and optionally rotated — render-only). Only the
  // BODY rotates; the floor pad + the icon/label above stay upright so the affordance stays legible.
  const body = buildPropBody(obj, art);
  body.x = obj.x;
  body.y = obj.y;
  if (obj.rotation) body.rotation = obj.rotation;
  map.addChild(body);

  // Dual-coded label (emoji icon + text) — sits above the prop, with the night-safe dark stroke so it
  // stays legible under every theme + the M4.2 night wash (ADR-0012). Never icon-only.
  const icon = new Text({ text: obj.icon, style: { fontSize: 22, fontFamily: 'system-ui' } });
  icon.anchor.set(0.5);
  icon.x = obj.x;
  icon.y = obj.y - 50;

  const label = new Text({
    text: obj.label,
    style: {
      fill: 0xeef1ff,
      fontSize: 11,
      fontWeight: '600',
      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
      stroke: { color: 0x0b1020, width: 3 },
      align: 'center',
    },
  });
  label.anchor.set(0.5, 0);
  label.x = obj.x;
  label.y = obj.y + 30;
  map.addChild(icon, label);
}

// ── Helpers ─────────────────────────────────────────────────────────────────────────────────────

